import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("wb_token");
    if (!token) { setLoading(false); return; }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (err) {
      // ONLY remove token on real 401 (Unauthorized).
      // Network errors, 500s, CORS issues, etc. should NOT log the user out —
      // preserves session across flaky networks, server restarts, deploys.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("wb_token");
      }
      // Otherwise keep token; next refresh will succeed when backend is healthy.
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Heartbeat (global) — keeps presence alive while user is logged in.
  // Per-channel updates happen from Community page directly via /presence/heartbeat.
  // Also re-fetches /auth/me every ~3 minutes so plan_tier / role / certified changes
  // made by the admin in the background propagate to the live UI without needing a refresh.
  useEffect(() => {
    if (!user) return;
    const ping = () => api.post("/presence/heartbeat", {}).catch(() => {});
    ping();
    const id = setInterval(ping, 30000);

    // Lightweight user-state refresher (6× slower than heartbeat → every 3 min)
    const refreshUser = () => {
      api.get("/auth/me").then((r) => {
        // Only update if something actually changed — avoids unnecessary re-renders.
        setUser((cur) => {
          if (!cur) return r.data;
          const nu = r.data;
          if (cur.plan_tier === nu.plan_tier &&
              cur.role === nu.role &&
              cur.certified === nu.certified &&
              cur.banned === nu.banned &&
              cur.subscription_until === nu.subscription_until &&
              cur.custom_title === nu.custom_title &&
              cur.custom_title_color === nu.custom_title_color) {
            return cur;
          }
          return nu;
        });
      }).catch(() => {});
    };
    const refreshId = setInterval(refreshUser, 180000); // 3 min

    // Also refresh whenever the tab regains focus (user comes back from another app)
    const onFocus = () => refreshUser();
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(id);
      clearInterval(refreshId);
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("wb_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (name, email, password, gdpr_consent = false, website = "") => {
    const lang = (typeof window !== "undefined" && localStorage.getItem("wb_lang")) || "sk";
    const r = await api.post("/auth/register", { name, email, password, lang, gdpr_consent, website });
    // Honeypot: if backend returns "honeypot" token, the request was silently rejected as a bot.
    // Treat like a generic failure so a real human accidentally triggering it can recover.
    if (r.data?.token === "honeypot" || !r.data?.user) {
      throw new Error("Registration failed");
    }
    localStorage.setItem("wb_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const setUserFromToken = (token, userObj) => {
    localStorage.setItem("wb_token", token);
    setUser(userObj);
  };

  const logout = () => {
    localStorage.removeItem("wb_token");
    setUser(null);
  };

  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (e) { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUserFromToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
